import type { AccountMode } from '@halo/webauth';
import type { BaseAccount, MnemonicAccount } from '@halo/wallet-app';
import type { IAppleClientParams, IGoogleClientParams, OauthUserInfo } from '@halo/webauth';
import type { GoogleOauth } from '@halo/webauth/googleOauth';

export enum StatusCode {
  OUTH_UN_POPUP = 4000,
  OUTH_POPUP_CANCEL = 4001,
  OUTH_LOGIN_CANCEL = 4002,
  OUTH_CLOUD_CANCEL = 4003,
  OUTH_UN_QUOTA = 4004, // Exceeding quota
}

export const StatusCodeMsg = {
  '4000': 'POPUP UNALLOWED',
  '4001': 'POPUP CANCEL',
  '4002': 'Login Cancel',
  '4003': 'Cloud Cancel',
  '4004': 'Exceeding quota',
};

export type Account = {
  uid?: string;
  clientUid?: string;
  type: string;
} & BaseAccount &
  Partial<Pick<MnemonicAccount, 'mnemonic' | 'nextAddressIndex'>>;

export interface SaveAccountArgs {
  account: Account;
}

export interface DeleteAccountArgs {
  id: string;
}

export interface DeleteCloudDataArgs {
  keys: UploadArgsDataKeys[];
}

export interface RequestCloudTokenArgs {
  oauthClient?: GoogleOauth;
  clientParams: GoogleInitClientArgs['clientParams'];
}

export interface SetExpiresTimeArgs {
  platform: CloudPlatform;
  cid: string;
  expiresTime: number;
}

export enum CloudPlatform {
  googleCloud = 'google',
  appleCloud = 'apple',
}

export enum CloudAccountType {
  google = 'google',
  apple = 'apple',
}

export interface Result {
  access_token: string;
  expires_time: number;
}

export interface GoogleTokenInfo {
  cid: string;
  sub: string;
  name?: string;
  picture?: string;
}

export interface AppleTokenInfo {
  id_token?: string;
}

interface ClientExtraParams {
  requestToken?: (params: {
    platform: CloudPlatform;
    code: string;
    id_token?: string;
  }) => Promise<GoogleTokenInfo | AppleTokenInfo>;
  onPopup?: () => void | Promise<void>;
  signInButton?: {
    id: string;
    theme?: 'medium';
    text?: string;
    onOk?: () => void;
  };
  signOutButton?: {
    id: string;
    theme?: 'medium';
    text?: string;
    onCancel?: () => void;
  };
}

export interface InitClientArgs<T, U> {
  mode?: AccountMode;
  cid?: string;
  key: string;
  authInfo?: AuthInfo;
  forcedUpdate?: boolean;
  clientParams: Partial<T> & U & ClientExtraParams;
}

export type GoogleInitClientArgs = InitClientArgs<
  IGoogleClientParams & { client_secret?: string; code?: string },
  Pick<IGoogleClientParams, 'client_id'>
>;
export type AppleInitClientArgs = InitClientArgs<
  IAppleClientParams,
  {
    redirect_uri: string;
    client_id: string;
  } & Pick<IAppleClientParams, 'apiToken' | 'containerIdentifier'>
>;

export type AccessInfoArgs = Required<
  Pick<IGoogleClientParams, 'client_id' | 'code' | 'client_secret' | 'redirect_uri'>
> & { grant_type?: 'authorization_code' | 'refresh_token'; refresh_token?: string };

export interface uploadArgs {
  isOldVersion?: boolean;
  data: {
    wallet?: {
      t: number;
      isChange: boolean;
      password: string;
      accounts: Account[];
      invalidAccounts?: Account[];
      backupCloudData?: string;
      version: number;
    };
    contacts?: string;
  };
}

export type UploadArgsDataKeys = keyof uploadArgs['data'];

export interface GetPasswordArgs {
  mode: AccountMode;
}

export interface IsLoginExpiredArgs {
  platform: CloudPlatform;
  cid: string;
}

export interface AuthInfo {
  id_token?: string;
  // Google
  access_token: string;
  expires_in?: number;
  expire?: number;
  refresh_token?: string;
  refresh_token_expires_in?: number;
  scope?: string;
  token_type?: string;
  cloud_token?: string;
  // Apple
  ckWebAuthToken?: string;
}

export abstract class Cloud {
  abstract authInfo?: AuthInfo;
  abstract platform: CloudPlatform;
  abstract logout(containerId?: string): void;
  abstract initClient(args: GoogleInitClientArgs | AppleInitClientArgs): Promise<AuthInfo>;
  abstract getCloudData: (args: { cid: string }) => Promise<Partial<uploadArgs['data']>>;
  abstract getPassword: () => Promise<{ password?: string; isChange?: boolean }>;
  abstract saveWalletAccount: (args: SaveAccountArgs) => Promise<any>;
  abstract deleteWalletAccount: (args: DeleteAccountArgs) => Promise<any>;
  abstract upload: (args: uploadArgs) => Promise<any>;
  abstract getUserInfo: () => Promise<OauthUserInfo>;
  abstract requestAuthRedirect: (args: any) => void;
  abstract getUserInfoByIdToken?: (id_token: string) => {};
  abstract cloudKitSetUpAuth?: () => Promise<any>;
  abstract deleteCloudData: (args: DeleteCloudDataArgs) => Promise<any>;
}

export type AccountModeType = AccountMode;

// Non-cloud storage
export abstract class NonCloud extends Cloud {}
